import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaUser, FaBuilding, FaClock, FaCheck, FaReply, FaSpinner } from 'react-icons/fa';
import apiService from '../../services/api';

const AdminContacts = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedContact, setSelectedContact] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetchContacts();
    }, []);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const data = await apiService.getContactSubmissions();
            setContacts(data);
        } catch (error) {
            console.error('Error fetching contacts:', error);
            setError('Failed to load contact submissions');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (contactId, status) => {
        try {
            await apiService.updateContactStatus(contactId, status);

            setContacts(contacts.map(contact =>
                contact.id === contactId
                    ? { ...contact, status, responded_at: status === 'responded' ? new Date().toISOString() : contact.responded_at }
                    : contact
            ));

            setMessage(`Contact marked as ${status}`);
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setError(error.message || 'Failed to update contact status');
            setTimeout(() => setError(''), 5000);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'new': return '#dc2626';
            case 'in_progress': return '#d97706';
            case 'responded': return '#059669';
            default: return '#64748b';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'new': return 'New';
            case 'in_progress': return 'In Progress';
            case 'responded': return 'Responded';
            default: return 'Unknown';
        }
    };

    if (loading) {
        return (
            <div className="admin-contacts">
                <div className="loading-container">
                    <FaSpinner className="spinner" />
                    <p>Loading contact submissions...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-contacts">
            <div className="page-header">
                <h1>Contact Management</h1>
                <p>Manage customer inquiries and support requests</p>
            </div>

            {message && (
                <div className="alert alert-success">
                    <FaCheck />
                    {message}
                </div>
            )}

            {error && (
                <div className="alert alert-error">
                    {error}
                </div>
            )}

            <div className="stats-cards">
                <div className="stat-card">
                    <h3>Total Contacts</h3>
                    <p className="stat-number">{contacts.length}</p>
                </div>
                <div className="stat-card">
                    <h3>New</h3>
                    <p className="stat-number new">{contacts.filter(c => c.status === 'new').length}</p>
                </div>
                <div className="stat-card">
                    <h3>In Progress</h3>
                    <p className="stat-number progress">{contacts.filter(c => c.status === 'in_progress').length}</p>
                </div>
                <div className="stat-card">
                    <h3>Responded</h3>
                    <p className="stat-number responded">{contacts.filter(c => c.status === 'responded').length}</p>
                </div>
            </div>

            <div className="contacts-container">
                <div className="contacts-list">
                    {contacts.length === 0 ? (
                        <div className="empty-state">
                            <FaEnvelope size={48} color="#cbd5e0" />
                            <p>No contact submissions found.</p>
                        </div>
                    ) : (
                        <div className="contacts-grid">
                            {contacts.map((contact) => (
                                <div
                                    key={contact.id}
                                    className={`contact-card ${selectedContact?.id === contact.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedContact(contact)}
                                >
                                    <div className="contact-header">
                                        <div className="contact-info">
                                            <h3>{contact.first_name} {contact.last_name}</h3>
                                            <p className="contact-email">{contact.email}</p>
                                            {contact.company && (
                                                <p className="contact-company">
                                                    <FaBuilding />
                                                    {contact.company}
                                                </p>
                                            )}
                                        </div>
                                        <div
                                            className="status-indicator"
                                            style={{ backgroundColor: getStatusColor(contact.status) }}
                                            title={getStatusText(contact.status)}
                                        />
                                    </div>

                                    <div className="contact-subject">
                                        <strong>{contact.subject}</strong>
                                    </div>

                                    <div className="contact-message">
                                        {contact.message.length > 150
                                            ? `${contact.message.substring(0, 150)}...`
                                            : contact.message
                                        }
                                    </div>

                                    <div className="contact-footer">
                                        <div className="contact-date">
                                            <FaClock />
                                            {formatDate(contact.created_at)}
                                        </div>
                                        <span className={`status-badge ${contact.status}`}>
                                            {getStatusText(contact.status)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {selectedContact && (
                    <div className="contact-details">
                        <div className="details-header">
                            <h2>Contact Details</h2>
                            <button
                                className="close-btn"
                                onClick={() => setSelectedContact(null)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="details-content">
                            <div className="detail-section">
                                <h3>Contact Information</h3>
                                <div className="detail-row">
                                    <span className="detail-label">Name:</span>
                                    <span>{selectedContact.first_name} {selectedContact.last_name}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Email:</span>
                                    <span>{selectedContact.email}</span>
                                </div>
                                {selectedContact.company && (
                                    <div className="detail-row">
                                        <span className="detail-label">Company:</span>
                                        <span>{selectedContact.company}</span>
                                    </div>
                                )}
                            </div>

                            <div className="detail-section">
                                <h3>Subject</h3>
                                <p>{selectedContact.subject}</p>
                            </div>

                            <div className="detail-section">
                                <h3>Message</h3>
                                <div className="message-content">
                                    {selectedContact.message}
                                </div>
                            </div>

                            <div className="detail-section">
                                <h3>Timestamps</h3>
                                <div className="detail-row">
                                    <span className="detail-label">Submitted:</span>
                                    <span>{formatDate(selectedContact.created_at)}</span>
                                </div>
                                {selectedContact.responded_at && (
                                    <div className="detail-row">
                                        <span className="detail-label">Responded:</span>
                                        <span>{formatDate(selectedContact.responded_at)}</span>
                                    </div>
                                )}
                            </div>

                            <div className="detail-section">
                                <h3>Actions</h3>
                                <div className="action-buttons">
                                    {selectedContact.status === 'new' && (
                                        <button
                                            className="btn btn-warning"
                                            onClick={() => handleStatusChange(selectedContact.id, 'in_progress')}
                                        >
                                            <FaClock />
                                            Mark In Progress
                                        </button>
                                    )}
                                    {selectedContact.status !== 'responded' && (
                                        <button
                                            className="btn btn-success"
                                            onClick={() => handleStatusChange(selectedContact.id, 'responded')}
                                        >
                                            <FaReply />
                                            Mark Responded
                                        </button>
                                    )}
                                    <a
                                        href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                                        className="btn btn-primary"
                                    >
                                        <FaEnvelope />
                                        Reply via Email
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .admin-contacts {
                    padding: 24px;
                }

                .page-header {
                    margin-bottom: 32px;
                }

                .page-header h1 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 8px;
                }

                .page-header p {
                    color: #64748b;
                    font-size: 16px;
                }

                .loading-container {
                    text-align: center;
                    padding: 80px 0;
                }

                .spinner {
                    animation: spin 1s linear infinite;
                    font-size: 24px;
                    color: #2563eb;
                    margin-bottom: 16px;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .alert {
                    padding: 16px 20px;
                    border-radius: 8px;
                    margin-bottom: 24px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-weight: 500;
                }

                .alert-success {
                    background: #d1fae5;
                    color: #065f46;
                    border: 1px solid #a7f3d0;
                }

                .alert-error {
                    background: #fee2e2;
                    color: #991b1b;
                    border: 1px solid #fca5a5;
                }

                .stats-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 20px;
                    margin-bottom: 32px;
                }

                .stat-card {
                    background: white;
                    padding: 24px;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    text-align: center;
                }

                .stat-card h3 {
                    font-size: 14px;
                    font-weight: 600;
                    color: #64748b;
                    margin-bottom: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .stat-number {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1a202c;
                    margin: 0;
                }

                .stat-number.new {
                    color: #dc2626;
                }

                .stat-number.progress {
                    color: #d97706;
                }

                .stat-number.responded {
                    color: #059669;
                }

                .contacts-container {
                    display: grid;
                    grid-template-columns: 1fr 400px;
                    gap: 24px;
                    min-height: 600px;
                }

                .contacts-list {
                    background: white;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                }

                .empty-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 80px 20px;
                    text-align: center;
                }

                .empty-state p {
                    color: #64748b;
                    font-size: 16px;
                    margin-top: 16px;
                }

                .contacts-grid {
                    max-height: 600px;
                    overflow-y: auto;
                }

                .contact-card {
                    padding: 20px;
                    border-bottom: 1px solid #f1f5f9;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .contact-card:hover {
                    background: #f8fafc;
                }

                .contact-card.selected {
                    background: #dbeafe;
                    border-left: 4px solid #2563eb;
                }

                .contact-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 12px;
                }

                .contact-info h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1a202c;
                    margin: 0 0 4px 0;
                }

                .contact-email {
                    color: #2563eb;
                    font-size: 14px;
                    margin: 0 0 4px 0;
                }

                .contact-company {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #64748b;
                    font-size: 14px;
                    margin: 0;
                }

                .status-indicator {
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }

                .contact-subject {
                    margin-bottom: 12px;
                    color: #374151;
                    font-size: 15px;
                }

                .contact-message {
                    color: #64748b;
                    font-size: 14px;
                    line-height: 1.5;
                    margin-bottom: 16px;
                }

                .contact-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .contact-date {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #64748b;
                    font-size: 12px;
                }

                .status-badge {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .status-badge.new {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .status-badge.in_progress {
                    background: #fef3c7;
                    color: #92400e;
                }

                .status-badge.responded {
                    background: #d1fae5;
                    color: #065f46;
                }

                .contact-details {
                    background: white;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    height: fit-content;
                    max-height: 600px;
                    overflow-y: auto;
                }

                .details-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    border-bottom: 1px solid #e5e7eb;
                    background: #f8fafc;
                }

                .details-header h2 {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1a202c;
                    margin: 0;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 24px;
                    color: #64748b;
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                }

                .close-btn:hover {
                    background: #e5e7eb;
                    color: #1a202c;
                }

                .details-content {
                    padding: 24px;
                }

                .detail-section {
                    margin-bottom: 24px;
                }

                .detail-section:last-child {
                    margin-bottom: 0;
                }

                .detail-section h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1a202c;
                    margin: 0 0 12px 0;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #f1f5f9;
                }

                .detail-row {
                    display: flex;
                    margin-bottom: 8px;
                }

                .detail-label {
                    font-weight: 600;
                    color: #374151;
                    min-width: 100px;
                    margin-right: 12px;
                }

                .message-content {
                    background: #f8fafc;
                    padding: 16px;
                    border-radius: 8px;
                    border: 1px solid #e5e7eb;
                    line-height: 1.6;
                    color: #374151;
                    white-space: pre-wrap;
                }

                .action-buttons {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .btn {
                    padding: 10px 16px;
                    border-radius: 8px;
                    border: none;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    text-decoration: none;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                }

                .btn-primary {
                    background: #2563eb;
                    color: white;
                }

                .btn-primary:hover {
                    background: #1d4ed8;
                }

                .btn-success {
                    background: #059669;
                    color: white;
                }

                .btn-success:hover {
                    background: #047857;
                }

                .btn-warning {
                    background: #d97706;
                    color: white;
                }

                .btn-warning:hover {
                    background: #b45309;
                }

                @media (max-width: 1024px) {
                    .contacts-container {
                        grid-template-columns: 1fr;
                    }

                    .contact-details {
                        max-height: none;
                    }
                }

                @media (max-width: 768px) {
                    .admin-contacts {
                        padding: 16px;
                    }

                    .stats-cards {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .page-header h1 {
                        font-size: 24px;
                    }

                    .contact-card {
                        padding: 16px;
                    }

                    .details-content {
                        padding: 16px;
                    }

                    .action-buttons {
                        flex-direction: column;
                    }

                    .btn {
                        justify-content: center;
                    }
                }

                @media (max-width: 480px) {
                    .stats-cards {
                        grid-template-columns: 1fr;
                    }

                    .contact-footer {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                    }

                    .detail-row {
                        flex-direction: column;
                    }

                    .detail-label {
                        min-width: auto;
                        margin-bottom: 4px;
                    }
                }
            `}</style>
        </div>
    );
};

export default AdminContacts;